import {Component, ElementRef, OnDestroy, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {DataService} from "../../services/data/data.service";
import {UiInteractionService} from "../../services/ui/ui.interaction.service";
import {ConfirmationDialogParameter} from "../dialogs/confirmation-dialog/confirmation-dialog.component";
import {NodeService} from "../../services/data/node.service";
import {TrainrunSectionService} from "../../services/data/trainrunsection.service";
import {EditorMode} from "../editor-menu/editor-mode";
import {FilterService} from "../../services/ui/filter.service";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {NoteService} from "../../services/data/note.service";
import {LabelRef, NetzgrafikDto} from "../../data-structures/business.data.structures";
import {LabelService} from "../../services/data/label.service";
import {LabelGroupService} from "../../services/data/labelgroup.service";
import {LabelGroup} from "../../models/labelGroup.model";
import {VersionControlService} from "../../services/data/version-control.service";
import {PositionTransformationService} from "../../services/util/position.transformation.service";
import {AutoLayoutService} from "../../services/util/auto-layout.service";
import {OrderingAlgorithm} from "../../data-structures/technical.data.structures";
import {SbbRadioChange} from "@sbb-esta/angular/radio-button";

@Component({
  selector: "sbb-editor-edit-tools-view-component",
  templateUrl: "./editor-edit-tools-view.component.html",
  styleUrls: ["./editor-edit-tools-view.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditorEditToolsViewComponent implements OnDestroy {
  @ViewChild("netzgrafikMergeFileInput", {static: false})
  netzgrafikMergeFileInput: ElementRef;
  @ViewChild("netzgrafikMergeAsACopyFileInput", {static: false})
  netzgrafikMergeAsACopyFileInput: ElementRef;

  public editorMode: EditorMode = EditorMode.NetzgrafikEditing;
  public nodeLabelGroups: LabelGroup[];
  public trainrunLabelGroups: LabelGroup[];
  private destroyed = new Subject<void>();

  orderingAlgorithmOptions = [
    {
      name: "app.view.editor-edit-tools-view-component.alphabeticalOrdering",
      title: "app.view.editor-edit-tools-view-component.alphabeticalOrderingTooltip",
      orderingAlgorithm: OrderingAlgorithm.Alphabetical,
    },
    {
      name: "app.view.editor-edit-tools-view-component.crossingAwareOrdering",
      title: "app.view.editor-edit-tools-view-component.crossingAwareOrderingTooltip",
      orderingAlgorithm: OrderingAlgorithm.ClutterAware,
    },
    {
      name: "app.view.editor-edit-tools-view-component.crossingAwarePushOrdering",
      title: "app.view.editor-edit-tools-view-component.crossingAwarePushOrderingTooltip",
      orderingAlgorithm: OrderingAlgorithm.ClutterAwarePushCrossings,
    },
  ];
  activeOrderingAlgorithm: OrderingAlgorithm = null;

  constructor(
    private dataService: DataService,
    private nodeService: NodeService,
    private trainrunSectionService: TrainrunSectionService,
    private noteService: NoteService,
    public labelService: LabelService,
    public labelGroupService: LabelGroupService,
    public filterService: FilterService,
    private uiInteractionService: UiInteractionService,
    private versionControlService: VersionControlService,
    private positionTransformationService: PositionTransformationService,
    private autoLayoutService: AutoLayoutService,
  ) {
    this.nodeLabelGroups = this.labelGroupService.getLabelGroupsFromLabelRef(LabelRef.Node);
    this.trainrunLabelGroups = this.labelGroupService.getLabelGroupsFromLabelRef(LabelRef.Trainrun);

    this.labelGroupService.labelGroups.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.nodeLabelGroups = this.labelGroupService.getLabelGroupsFromLabelRef(LabelRef.Node);
      this.trainrunLabelGroups = this.labelGroupService.getLabelGroupsFromLabelRef(
        LabelRef.Trainrun,
      );
    });

    this.labelService.labels.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.nodeLabelGroups = this.labelGroupService.getLabelGroupsFromLabelRef(LabelRef.Node);
      this.trainrunLabelGroups = this.labelGroupService.getLabelGroupsFromLabelRef(
        LabelRef.Trainrun,
      );
    });

    this.activeOrderingAlgorithm = this.uiInteractionService.getActiveOrderingAlgorithm();
    this.nodeService.nodes.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.activeOrderingAlgorithm = this.uiInteractionService.getActiveOrderingAlgorithm();
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  getVariantIsWritable() {
    return this.versionControlService.getVariantIsWritable();
  }

  getAreMultiObjectSelected(): boolean {
    return this.uiInteractionService.getEditorMode() === EditorMode.MultiNodeMoving;
  }

  onClearAllFiltered() {
    const confirmationDialogParameter = new ConfirmationDialogParameter(
      $localize`:@@app.view.editor-edit-tools-view-component.delete:Delete`,
      $localize`:@@app.view.editor-edit-tools-view-component.on-clear-delete-all-non-visible-elements:Should all non-visible elements be permanently deleted from the netzgrafik?`,
    );
    this.uiInteractionService
      .showConfirmationDiagramDialog(confirmationDialogParameter)
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.trainrunSectionService.deleteAllNonVisibleTrainrunSections();
          this.nodeService.deleteAllNonVisibleNodes();
          this.noteService.deleteAllNonVisibleNotes();
        }
      });
  }

  onClear() {
    const confirmationDialogParameter = new ConfirmationDialogParameter(
      $localize`:@@app.view.editor-edit-tools-view-component.delete:Delete`,
      $localize`:@@app.view.editor-edit-tools-view-component.on-clear-delete-all-visible-elements:Should all visible elements be permanently deleted from the netzgrafik?`,
    );
    this.uiInteractionService
      .showConfirmationDiagramDialog(confirmationDialogParameter)
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.trainrunSectionService.deleteAllVisibleTrainrunSections();
          this.nodeService.deleteAllVisibleNodes();
          this.noteService.deleteAllVisibleNotes();
        }
      });
  }

  onClearAllTrainruns() {
    const confirmationDialogParameter = new ConfirmationDialogParameter(
      $localize`:@@app.view.editor-edit-tools-view-component.delete:Delete`,
      $localize`:@@app.view.editor-edit-tools-view-component.on-clear-delete-all-visible-trainruns:Should all visible trainruns be permanently deleted from the netzgrafik?`,
    );
    this.uiInteractionService
      .showConfirmationDiagramDialog(confirmationDialogParameter)
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.trainrunSectionService.deleteAllVisibleTrainrunSections();
        }
      });
  }

  onClearAllNotes() {
    const confirmationDialogParameter = new ConfirmationDialogParameter(
      $localize`:@@app.view.editor-edit-tools-view-component.delete:Delete`,
      $localize`:@@app.view.editor-edit-tools-view-component.on-clear-delete-all-visible-notes:Should all visible notes be permanently deleted from the netzgrafik?`,
    );
    this.uiInteractionService
      .showConfirmationDiagramDialog(confirmationDialogParameter)
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.noteService.deleteAllVisibleNotes();
        }
      });
  }

  onLoadNetzgrafikToInsertCopyButton() {
    this.netzgrafikMergeAsACopyFileInput.nativeElement.click();
  }

  onLoadNetzgrafikToMergeButton() {
    this.netzgrafikMergeFileInput.nativeElement.click();
  }

  onLoadNetzgrafikToMergeAsACopy(event: Event) {
    this.uiInteractionService.closeNodeBaseData();
    this.uiInteractionService.closePerlenkette();
    this.loadNetzgrafik(event, (netzgrafikDto) =>
      this.dataService.insertCopyNetzgrafikDto(netzgrafikDto),
    );
  }

  onLoadNetzgrafikToMerge(event: Event) {
    this.uiInteractionService.closeNodeBaseData();
    this.uiInteractionService.closePerlenkette();
    this.loadNetzgrafik(event, (netzgrafikDto) =>
      this.dataService.mergeNetzgrafikDto(netzgrafikDto),
    );
  }

  onUpdateOrderingAlgorithm(event: SbbRadioChange) {
    this.uiInteractionService.setActiveOrderingAlgorithm(event.value);
  }

  onAlignElementsLeft() {
    this.positionTransformationService.alignSelectedElementsToLeftBorder();
  }

  onAlignElementsTop() {
    this.positionTransformationService.alignSelectedElementsToTopBorder();
  }

  onAlignElementsRight() {
    this.positionTransformationService.alignSelectedElementsToRightBorder();
  }

  onAlignElementsBottom() {
    this.positionTransformationService.alignSelectedElementsToBottomBorder();
  }

  onInverseOptimizeLayout() {
    this.autoLayoutService.optimizeLayout(true);
  }

  onOptimizeLayout() {
    this.autoLayoutService.optimizeLayout(false);
  }

  private loadNetzgrafik(event: Event, callback: (dto: NetzgrafikDto) => void) {
    const fileInput = event.target;
    if (!(fileInput instanceof HTMLInputElement)) {
      throw new Error("Event target is not a file input");
    }

    const file = fileInput.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const netzgrafikDto = JSON.parse(reader.result.toString());
      if (
        "nodes" in netzgrafikDto &&
        "trainrunSections" in netzgrafikDto &&
        "trainruns" in netzgrafikDto &&
        "resources" in netzgrafikDto &&
        "metadata" in netzgrafikDto
      ) {
        this.setEditModeToNetzgrafikEditing();
        callback(netzgrafikDto);
      }
    };
    reader.readAsText(file);

    // set the event target value to null in order to be able to load the same file multiple times after one another
    fileInput.value = null;
  }

  private setEditModeToNetzgrafikEditing() {
    if (this.editorMode !== EditorMode.NetzgrafikEditing) {
      this.editorMode = EditorMode.NetzgrafikEditing;
      this.uiInteractionService.showNetzgrafik();
    }
  }
}
